import { Component, EventEmitter, OnDestroy, OnInit, Output, inject, DestroyRef, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import { GtwElementsBaseUrlsModel } from '../../../shared/_models/gtw-elements-base-urls.model';
import { NaicsMaintenanceService } from './naics-maintenance.service';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  firstGridRowObjectFromPayload,
  generalConfigFromGlobal,
  normalizeFilterParams,
  rowPayloadFromActiveScreen,
} from '../../../shared/workspace-element-context';

@Component({
    selector: 'gtw-naics-maintenance',
    templateUrl: './naics-maintenance.component.html',
    styleUrls: ['./naics-maintenance.component.scss'],
    standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class NaicsMaintenanceComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);


  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  private readonly subs = new Subscription();

  loader: boolean = false;
  naicsForm!: UntypedFormGroup;
  isEdit: boolean = false;
  minimise: boolean = false;

  rowData: rowData = { CODE_SET_KEY: null, CODE_NAME: '', CODE_DESC: '' };
  baseURLs: GtwElementsBaseUrlsModel = { admin: '', api: '', custom: '', efile: '', pdfEngine: '', bulkPdfEngine: '' };
  baseGlobalParams: any = {};
  filterParams: any = {};
  USER_SETTINGS: any = {};

  scenarioCode: string = '';
  isIssueKeyEnabled: string = '';
  issueKey: number = 0;

  constructor(
    private naicsService: NaicsMaintenanceService,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
  ) {
    this.naicsForm = new UntypedFormGroup({
      code: new UntypedFormControl('', Validators.compose([
        Validators.required,
        Validators.pattern("^[0-9]{6}$")
      ])),
      codeDesc: new UntypedFormControl('', [Validators.required, Validators.maxLength(100)]),
    });
  }

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => {
      this.applyWorkspaceContext();
      this.initialize();
    }));
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => {
      this.applyWorkspaceContext();
      this.initialize();
    }));
    this.initialize();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);

    this.baseURLs = generalConfigFromGlobal(this.gtwGlobal);
    this.baseGlobalParams = header.globalParams || {};
    this.USER_SETTINGS = header.userSettingsData || {};
    this.filterParams = normalizeFilterParams(activeScreen);
    this.rowData = firstGridRowObjectFromPayload(rowPayloadFromActiveScreen(activeScreen)) as rowData;

    this.scenarioCode = this.baseGlobalParams?.scenarioCode || '';
    this.isIssueKeyEnabled = this.filterParams?.is_issue_key_enabled
      || this.baseGlobalParams?.is_issue_key_enabled
      || 'N';
    this.issueKey = Number(this.baseGlobalParams?.issue_key || this.filterParams?.issue_key || 0);
  }

  initialize(): void {
    const code = this.rowData?.CODE_NAME || '';
    const rawDesc = this.rowData?.CODE_DESC || '';
    const prefix = `${code}-`;
    const desc = rawDesc.startsWith(prefix) ? rawDesc.substring(prefix.length) : rawDesc;
    this.naicsForm.patchValue({
      code,
      codeDesc: desc
    });
    this.isEdit = !!this.rowData?.CODE_SET_KEY;
  }

  insert(): void {
    if (this.naicsForm.status === 'INVALID') {
      this.naicsService.openSnackBar("Please enter all the required fields!", 'gtw-snackbar--red');
      return;
    }
    this.loader = true;
    const params: saveParams = {
      tax_year: this.baseGlobalParams?.tax_year,
      scenario: this.baseGlobalParams?.scenario,
      jcd_key: this.baseGlobalParams?.jcd_key
    };
    const clob_settings: saveParams = { tax_year: params.tax_year, scenario: params.scenario, jcd_key: params.jcd_key };
    const clob_data: rowData[] = [{
      CODE_SET_KEY: this.isEdit ? this.rowData.CODE_SET_KEY : null,
      CODE_NAME: this.naicsForm.value.code,
      CODE_DESC: `${this.naicsForm.value.code}-${this.naicsForm.value.codeDesc}`,
    }];
    this.naicsService.saveNaicsCode(this.baseURLs.api, params, clob_settings, clob_data, this.scenarioCode, this.isIssueKeyEnabled, this.issueKey).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data) => {
      if (data !== 'error') {
        this.requestGridUpdate.emit('refresh');
        this.loader = false;
      } else {
        console.log("Error while saving");
        this.loader = false;
      }
    }, () => {
      console.log("Error while saving");
      this.loader = false;
    });
  }

  cancel(): void {
    this.requestGridUpdate.emit('cancel');
  }
}

export type rowData = {
  CODE_SET_KEY: string | null | undefined;
  CODE_NAME: string | undefined;
  CODE_DESC: string | undefined;
}

export type saveParams = {
  tax_year: string | undefined;
  scenario: string | undefined;
  jcd_key: string | undefined;
}
